import { View } from "react-native";
import { FileText, Plus } from "lucide-react-native";
import { SectionTitle } from "@louise/moon-ui/primitives/SectionTitle";
import { EMBER } from "../../../../onboarding/theme";
import type { Document } from "@src/packages/documents/hooks/useDocuments";
import { OrganicMiniIcon } from "@src/packages/homepage/plan/components/OrganicMiniIcon";
import ViewAllLink from "../../../components/ViewAllLink";
import { DocumentRow } from "../../documents/DocumentRow";
import { useTranslations, type TranslationFunction } from "@src/packages/i18n";
import { useLocale } from "@src/packages/i18n/useLocale";
import { formatDate } from "@src/packages/i18n/formatters";
import type { Locale } from "@src/packages/i18n/locale";

export const DOCUMENT_CYCLE_TAG_PREFIX = "cycle:";

export function getCycleDocumentTag(cycleId: string) {
  return `${DOCUMENT_CYCLE_TAG_PREFIX}${cycleId}`;
}

export function getCycleDocumentFilterLabel({
  cycleType,
  cycleNumber,
}: {
  cycleType: string | null | undefined;
  cycleNumber?: number | null;
}) {
  const baseLabel = (cycleType ?? "Cycle").replace(/\s+Cycle$/i, "");
  return cycleNumber ? `${baseLabel} #${cycleNumber}` : baseLabel;
}

function getDocumentDisplayDate(document: Document) {
  return document.documentDate ?? document.createdAt ?? null;
}

function formatDocumentDate(
  date: string | null,
  t: TranslationFunction,
  locale: Locale,
) {
  if (!date) return t("care.cycle_documents.no_date");
  return formatDate(new Date(date), locale, {
    month: "short",
    day: "numeric",
  });
}

function getDocumentDateLabel(
  document: Document,
  t: TranslationFunction,
  locale: Locale,
) {
  return formatDocumentDate(getDocumentDisplayDate(document), t, locale);
}

function getDocumentSubtitle(document: Document, t: TranslationFunction) {
  if (document.tags.includes("doctype:lab"))
    {return t("care.cycle_documents.doc_lab_record");}
  if (document.tags.includes("doctype:imaging"))
    {return t("care.cycle_documents.doc_imaging_report");}
  if (document.tags.includes("doctype:treatment_plan")) {
    return t("care.cycle_documents.doc_treatment_plan");
  }
  if (document.tags.includes("doctype:genetic"))
    {return t("care.cycle_documents.doc_genetic_report");}
  if (document.tags.includes("admin"))
    {return t("care.cycle_documents.doc_billing");}
  if (document.tags.includes("legal_consent"))
    {return t("care.cycle_documents.doc_consent");}
  if (document.tags.includes("coverage_benefits"))
    {return t("care.cycle_documents.doc_coverage");}
  return t("care.cycle_documents.doc_default");
}

export function getCycleDocuments(
  documents: Document[] | undefined,
  cycleId: string | null | undefined,
) {
  if (!documents || !cycleId) return [];
  const cycleTag = getCycleDocumentTag(cycleId);
  return documents
    .filter((document) => document.tags.includes(cycleTag))
    .sort((a, b) => {
      const dateA = getDocumentDisplayDate(a) ?? "";
      const dateB = getDocumentDisplayDate(b) ?? "";
      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;
      return dateB.localeCompare(dateA);
    });
}

export default function CycleRelatedDocumentsSection({
  documents,
  onViewAll,
  onUpload,
  title,
}: {
  documents: Document[];
  onViewAll: () => void;
  onUpload: () => void;
  title?: string;
}) {
  const t = useTranslations();
  const { locale } = useLocale();
  const sectionTitle = title ?? t("care.cycle_documents.section_title");
  const previewDocuments = documents.slice(0, 3);

  return (
    <View>
      <View
        style={{
          marginTop: 28,
          marginBottom: 10,
          marginLeft: 4,
          marginRight: 2,
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <SectionTitle icon={FileText}>{sectionTitle}</SectionTitle>
        <ViewAllLink
          label={t("care.cycle_documents.view_all")}
          onPress={onViewAll}
        />
      </View>

      <View style={{ gap: 8 }}>
        {previewDocuments.length > 0 ? (
          previewDocuments.map((document) => (
            <DocumentRow
              key={document.id}
              title={document.name}
              subtitle={getDocumentSubtitle(document, t)}
              dateLabel={getDocumentDateLabel(document, t, locale)}
              icon={
                <FileText
                  size={18}
                  color={EMBER.colors.textLight}
                  strokeWidth={1.5}
                />
              }
              onPress={onViewAll}
            />
          ))
        ) : (
          <DocumentRow
            title={t("care.cycle_documents.empty_title")}
            subtitle={t("care.cycle_documents.empty_subtitle")}
            icon={<OrganicMiniIcon kind="medical-records" size={34} />}
            trailing={
              <Plus
                size={18}
                color={EMBER.colors.textLight}
                strokeWidth={1.5}
              />
            }
            onPress={onUpload}
            showChevron={false}
          />
        )}
      </View>
    </View>
  );
}
